import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const chargeOrPause = async (id, onDone) => {
    if (!window.confirm("Charge this customer's saved card now? If it fails, the line is paused and they're texted a pay link.")) return;
    try {
        const { data } = await api.post(`/admin/mobile/${id}/charge-or-pause`);
        if (data.charged) toast.success(`Charged ${money(data.amount)} — line active`);
        else toast.error(`Not charged (${data.reason}) — line paused & pay link texted`);
        onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Charge failed"); }
};

function ProfitCard() {
    const [p, setP] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        api.get("/admin/profit").then(({ data }) => setP(data)).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load profit"));
    }, []);
    const cell = (label, value, testid, tone = "") => (
        <div className="border border-[#EDE7F6] rounded-sm p-3">
            <p className="font-mono text-[10px] uppercase text-zinc-500">{label}</p>
            <p className={`font-display text-xl ${tone}`} data-testid={testid}>{value}</p>
        </div>
    );
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid="admin-profit-card">
            <p className="text-sm font-medium">Profit this month {p && <span className="text-zinc-500 font-normal">· {p.month}</span>}</p>
            <p className="text-xs text-zinc-500 mb-3">Real money Stripe collected vs what Telnyx has cost so far this month.</p>
            {err && <p className="text-xs text-[#D97706]" data-testid="admin-profit-error">{err}</p>}
            {!p && !err && <p className="text-xs text-zinc-500">Loading…</p>}
            {p && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {cell("Collected (after fees & refunds)", money(p.stripe_net), "profit-stripe-net")}
                    {cell("Telnyx spend to date", money(p.telnyx_total), "profit-telnyx-total")}
                    {cell("Profit", money(p.profit), "profit-total", p.profit >= 0 ? "text-green-700" : "text-[#D97706]")}
                    {cell("Charges", p.charges, "profit-charges")}
                </div>
            )}
        </div>
    );
}

function ProfitTrendCard() {
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => { api.get("/admin/profit/trend").then(({ data }) => setRows(data.months || [])).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load trend")); }, []);
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid="admin-profit-trend">
            <p className="text-sm font-medium">Profit trend · last 6 months</p>
            <p className="text-xs text-zinc-500 mb-3">Stripe money collected (after fees) vs Telnyx spend. Telnyx spend is recorded from this month on — earlier months show income only.</p>
            {err && <p className="text-xs text-red-600">{err}</p>}
            {!rows && !err && <p className="text-xs text-zinc-500">Loading…</p>}
            {rows && (
                <div className="h-64" data-testid="admin-profit-trend-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={rows} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#EDE7F6" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                            <Tooltip formatter={(v) => (v === null || v === undefined ? "not tracked" : money(v))} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="stripe_net" name="Collected" fill="#4C1D95" radius={[3, 3, 0, 0]} />
                            <Bar dataKey="telnyx" name="Telnyx spend" fill="#D97706" radius={[3, 3, 0, 0]} />
                            <Bar dataKey="profit" name="Profit" fill="#059669" radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

function QuizStatsCard() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/admin/quiz/stats").then(({ data }) => setD(data)).catch(() => {}); }, []);
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid="admin-quiz-stats">
            <p className="text-sm font-medium">Plan quiz results</p>
            <p className="text-xs text-zinc-500 mb-3">{d ? `${d.quizzes_completed} quiz(zes) completed · ${d.paid_total} went on to pay` : "Loading…"}</p>
            {d && (
                <table className="w-full text-sm">
                    <thead><tr className="text-left font-mono text-[10px] uppercase text-zinc-500"><th className="py-1">Plan</th><th>Recommended</th><th>Chose</th><th>Paid</th><th>Conversion</th></tr></thead>
                    <tbody>
                        {d.plans.map((p) => (
                            <tr key={p.plan_id} className="border-t border-[#EDE7F6]" data-testid={`quiz-row-${p.plan_id}`}>
                                <td className="py-1.5">{p.name}</td><td>{p.recommended}</td><td>{p.chose}</td><td>{p.paid}</td>
                                <td>{p.recommended ? `${Math.round((p.paid / p.recommended) * 100)}%` : "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function ScanStatsCard() {
    const [rows, setRows] = useState(null);
    useEffect(() => { api.get("/admin/scans/stats").then(({ data }) => setRows(data.sources || [])).catch(() => setRows([])); }, []);
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid="admin-scan-stats">
            <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Poster &amp; QR scans</p>
                <a href="/poster" target="_blank" rel="noreferrer" className="text-xs text-[#4C1D95] link-underline" data-testid="admin-open-poster">Open printable poster →</a>
            </div>
            <p className="text-xs text-zinc-500 mb-3">Each scan of the poster QR code is counted, plus customers from it who paid.</p>
            {!rows ? <p className="text-xs text-zinc-500">Loading…</p> : rows.length === 0 ? <p className="text-xs text-zinc-500" data-testid="admin-scan-empty">No scans yet — print the poster and put it up.</p> : (
                <table className="w-full text-sm">
                    <thead><tr className="text-left font-mono text-[10px] uppercase text-zinc-500"><th className="py-1">Source</th><th>Scans</th><th>Paid customers</th><th>Revenue</th></tr></thead>
                    <tbody>{rows.map((r) => (
                        <tr key={r.src} className="border-t border-[#EDE7F6]" data-testid={`scan-row-${r.src}`}>
                            <td className="py-1.5">{r.src}</td><td>{r.scans}</td><td>{r.paid}</td><td>{money(r.revenue)}</td>
                        </tr>))}</tbody>
                </table>
            )}
        </div>
    );
}

function UnpaidLinesCard({ refreshKey, onChange }) {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get("/admin/mobile/unpaid").then(({ data }) => setRows(data.lines || [])).catch(() => {}); }, [refreshKey]);
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid="admin-unpaid-lines">
            <p className="text-sm font-medium">Unpaid lines <span className="text-zinc-500 font-normal" data-testid="admin-unpaid-count">({rows.length})</span></p>
            <p className="text-xs text-zinc-500 mb-3">Paused until the customer pays. Their service turns back on automatically after payment.</p>
            {rows.length === 0 ? <p className="text-xs text-zinc-500">No unpaid lines.</p> : (
                <div className="divide-y divide-[#EDE7F6] max-h-80 overflow-auto">
                    {rows.map((r) => (
                        <div key={r.mobile_sub_id} className="py-2 flex items-center justify-between gap-3" data-testid={`unpaid-row-${r.mobile_sub_id}`}>
                            <div className="min-w-0">
                                <p className="text-sm truncate">{r.name || "—"} <span className="text-zinc-500 font-mono text-xs">{r.email}</span></p>
                                <p className="text-xs text-zinc-500 font-mono">{r.phone_number || "—"} · {r.days_unpaid ?? "?"} day(s) unpaid · reminders: {r.reminders_sent?.length ? r.reminders_sent.map((d) => `day ${d}`).join(", ") : "none yet"}</p>
                            </div>
                            <Button onClick={() => chargeOrPause(r.mobile_sub_id, onChange)} data-testid={`unpaid-charge-${r.mobile_sub_id}`}
                                variant="outline" className="rounded-sm h-8 text-xs border-[#DDD6FE] shrink-0">Charge card</Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function PortCheckCard() {
    const [orderId, setOrderId] = useState("34945918-65c0-4390-a32a-82d26720ed56");
    const [res, setRes] = useState(null);
    const [busy, setBusy] = useState(false);
    const check = async () => {
        setBusy(true);
        try { const { data } = await api.post(`/admin/porting/order/${orderId.trim()}/check`); setRes(data); }
        catch (e) { toast.error(e?.response?.data?.detail || "Check failed"); }
        setBusy(false);
    };
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid="admin-port-check">
            <p className="text-sm font-medium">Check a number transfer</p>
            <p className="text-xs text-zinc-500 mb-3">Looks up the Telnyx transfer order and submits it if it's still a draft (paid customer + signed LOA).</p>
            <div className="flex gap-2">
                <input value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="port-check-input"
                    className="border border-[#DDD6FE] rounded-sm px-3 h-10 text-sm bg-white outline-none focus:border-[#4C1D95] flex-1 font-mono" />
                <Button onClick={check} disabled={busy || !orderId.trim()} data-testid="port-check-btn" className="bg-[#4C1D95] text-white">{busy ? "Checking…" : "Check & submit"}</Button>
            </div>
            {res && (
                <p className="text-xs mt-2 font-mono" data-testid="port-check-result">
                    Status: {res.telnyx_status || "—"} · {res.phone_numbers?.join(", ")} · {res.note}
                </p>
            )}
        </div>
    );
}

function CleanupCard({ onChange }) {
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = () => api.get("/admin/cleanup-unpaid/preview").then(({ data }) => setPreview(data)).catch(() => {});
    useEffect(() => { load(); }, []);
    const run = async () => {
        const typed = window.prompt(`This cancels ${preview?.lines ?? 0} line(s) at Telnyx and permanently deletes ${preview?.accounts ?? 0} unpaid account(s). Payment records are kept. Type DELETE to confirm.`);
        if (typed !== "DELETE") return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/cleanup-unpaid", { confirm: "DELETE" });
            toast.success(`Deleted ${data.deleted_accounts} account(s) · cancelled ${data.cancelled_lines} line(s) · released ${data.released_numbers} number(s)`);
            load(); onChange && onChange();
        } catch (e) { toast.error(e?.response?.data?.detail || "Clean-up failed"); }
        setBusy(false);
    };
    return (
        <div className="border border-red-300 rounded-sm bg-paper p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="admin-cleanup-card">
            <div>
                <p className="text-sm font-medium">Clean up unpaid accounts</p>
                <p className="text-xs text-zinc-500" data-testid="admin-cleanup-preview">
                    {preview ? `${preview.accounts} account(s) never paid · ${preview.lines} line(s) to cancel at Telnyx. Admins, dealers, paying customers and sign-ups from the last 2 days are kept.` : "Checking…"}
                </p>
            </div>
            <Button onClick={run} disabled={busy || !preview || preview.accounts === 0} data-testid="admin-cleanup-btn" className="bg-red-600 hover:bg-red-700 text-white shrink-0">
                {busy ? "Cleaning up…" : "Cancel & delete unpaid"}
            </Button>
        </div>
    );
}

export default function PayFirstPanel({ refreshKey, onChange }) {
    return (
        <div className="grid lg:grid-cols-2 gap-3">
            <ProfitCard />
            <PortCheckCard />
            <div className="lg:col-span-2"><ProfitTrendCard /></div>
            <div className="lg:col-span-2"><QuizStatsCard /></div>
            <div className="lg:col-span-2"><ScanStatsCard /></div>
            <div className="lg:col-span-2"><UnpaidLinesCard refreshKey={refreshKey} onChange={onChange} /></div>
            <div className="lg:col-span-2"><CleanupCard onChange={onChange} /></div>
        </div>
    );
}
